import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Title1,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Button,
  Spinner,
  Tooltip,
} from '@fluentui/react-components'
import { Warning20Regular, Link16Regular, PeopleTeam16Regular, Building16Regular } from '@fluentui/react-icons'
import { apiFetchJson } from '../lib/apiFetch'
import { ATTRIBUTION_COLOR, EXTERNAL_BADGE_STYLE } from '../lib/attributionColors'
import { getLicenseIcon } from '../lib/licenseIcons'
import { SERVICE_LABELS } from '../lib/services'
import { useLicenseFilters } from '../lib/useLicenseFilters'
import {
  buildRows,
  filterPersonRows,
  hasExternalAccounts,
  hasLinkedAccounts,
  hasSharedAccounts,
  isFullyExternal,
  isMissingOwner,
  licenseTypeDisplayName,
} from '../lib/personRows'
import LicenseFilterBar from '../components/LicenseFilterBar'
import StatusBadge from '../components/StatusBadge'
import Lozenge from '../components/Lozenge'
import type { GitHubResponse, GitHubUserRow, LicensesResponse, LicenseTypeConfig, LicenseTypesResponse } from '../types'

/** Own linked GitHub account, and/or accounts this person is the named Responsible for
 * (external/shared) — same "who's accountable" concept the Status column's Link/Shared/External
 * icons already show for licenses, just sourced from GitHub's own link table instead of
 * ServiceAssignments (GitHub isn't a licensed service — see docs/ARCHITECTURE.md's "GitHub access
 * sync"). Built once from /api/github's flat user list, keyed by personEmail. */
function useGitHubByPerson() {
  const [users, setUsers] = useState<GitHubUserRow[]>([])

  useEffect(() => {
    apiFetchJson<GitHubResponse>('/api/github')
      .then(res => setUsers(res.users))
      .catch(() => {})
  }, [])

  const ownAccountByEmail = useMemo(() => {
    const map = new Map<string, GitHubUserRow>()
    for (const u of users) {
      if (u.link?.status === 'linked' && u.link.personEmail) map.set(u.link.personEmail, u)
    }
    return map
  }, [users])

  const responsibleForByEmail = useMemo(() => {
    const map = new Map<string, GitHubUserRow[]>()
    for (const u of users) {
      if (!u.link?.responsibleEmail) continue
      const list = map.get(u.link.responsibleEmail) ?? []
      list.push(u)
      map.set(u.link.responsibleEmail, list)
    }
    return map
  }, [users])

  return { ownAccountByEmail, responsibleForByEmail }
}

const STORAGE_KEY = 'licenses-filters'

export default function Licenses() {
  const [data, setData] = useState<LicensesResponse | null>(null)
  const [configByService, setConfigByService] = useState<Record<string, Record<string, LicenseTypeConfig>>>({})
  const [error, setError] = useState<string | null>(null)
  const { filters, updateFilters } = useLicenseFilters(STORAGE_KEY)
  const { ownAccountByEmail, responsibleForByEmail } = useGitHubByPerson()
  const navigate = useNavigate()

  useEffect(() => {
    apiFetchJson<LicensesResponse>('/api/licenses')
      .then(setData)
      .catch(e => setError(String(e)))
  }, [])

  useEffect(() => {
    if (!data) return
    const serviceIds = [...new Set(data.results.map(r => r.serviceId))]
    Promise.all(
      serviceIds.map(id =>
        apiFetchJson<LicenseTypesResponse>(`/api/license-types?serviceId=${id}`).then(res => [id, res] as const),
      ),
    )
      .then(entries => {
        const map: Record<string, Record<string, LicenseTypeConfig>> = {}
        for (const [id, res] of entries) {
          map[id] = Object.fromEntries(res.types.map(t => [t.licenseType, t]))
        }
        setConfigByService(map)
      })
      .catch(e => setError(String(e)))
  }, [data])

  const rows = useMemo(() => (data ? buildRows(data.persons, data.results) : []), [data])
  // When the toggle is on, strip shared/external assignments from byService entirely (not just at
  // render time) so "Show users without licenses" (off by default) correctly hides a row whose
  // only licenses were shared/external ones now hidden — filtering only the rendered icons would
  // leave such a row visible with an empty cell, contradicting that switch.
  const displayRows = useMemo(() => {
    if (!filters.hideAttributedLicenses) return rows
    return rows.map(row => ({
      ...row,
      byService: Object.fromEntries(
        Object.entries(row.byService).map(([id, assignments]) => [
          id,
          assignments.filter(a => a.resolutionKind !== 'shared' && a.resolutionKind !== 'external'),
        ]),
      ),
    }))
  }, [rows, filters.hideAttributedLicenses])
  const serviceIds = useMemo(() => (data ? [...new Set(data.results.map(r => r.serviceId))] : []), [data])
  const filteredRows = useMemo(() => filterPersonRows(displayRows, filters), [displayRows, filters])

  // Counted per account (distinct raw email) and only for accounts holding a real license, so
  // this matches the Dashboard's per-service unresolvedCount and the "Unresolved" filter the
  // button deep-links into — counting license rows instead would overstate it.
  const unresolvedByService = useMemo(() => {
    const byService = new Map<string, Map<string, { real: boolean; unresolved: boolean }>>()
    for (const r of data?.results ?? []) {
      const perService = byService.get(r.serviceId) ?? new Map()
      const entry = perService.get(r.rawEmail) ?? { real: false, unresolved: true }
      if (!r.excludeFromSeatCount) entry.real = true
      if (r.resolutionKind !== 'unresolved') entry.unresolved = false
      perService.set(r.rawEmail, entry)
      byService.set(r.serviceId, perService)
    }
    const counts: Record<string, number> = {}
    for (const [serviceId, perService] of byService) {
      let n = 0
      for (const entry of perService.values()) if (entry.real && entry.unresolved) n++
      if (n > 0) counts[serviceId] = n
    }
    return counts
  }, [data])
  const unresolvedTotal = Object.values(unresolvedByService).reduce((sum, n) => sum + n, 0)
  const firstUnresolvedServiceId = Object.keys(unresolvedByService)[0]

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Title1>Licenses</Title1>
        {unresolvedTotal > 0 && (
          <Button
            appearance="outline"
            icon={<Warning20Regular />}
            onClick={() => firstUnresolvedServiceId && navigate(`/services/${firstUnresolvedServiceId}?tab=links&problem=unresolved`)}
          >
            {unresolvedTotal} unresolved
          </Button>
        )}
      </div>

      {error && <p>Error: {error}</p>}
      {!data && !error && <Spinner label="Loading licenses..." />}

      {data && (
        <>
          <LicenseFilterBar
            serviceIds={serviceIds}
            results={data.results}
            configByService={configByService}
            filters={filters}
            onChange={updateFilters}
            showAttributionToggle
          />

          <Table style={{ width: '100%' }}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Person</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                {serviceIds.map(id => (
                  <TableHeaderCell key={id}>{SERVICE_LABELS[id] ?? id}</TableHeaderCell>
                ))}
                <TableHeaderCell>GitHub</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRows.map(row => (
                <TableRow
                  key={row.personEmail}
                  onClick={() => navigate(`/persons/${encodeURIComponent(row.personEmail)}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Tooltip content={row.personDisplayName} relationship="label" withArrow>
                      <div style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.personDisplayName}
                      </div>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <StatusBadge status={row.entraStatus} external={isFullyExternal(row)} />
                        {hasLinkedAccounts(row) && (
                          <Tooltip
                            content="Includes one or more manually-linked licenses attached to this account"
                            relationship="description"
                            withArrow
                          >
                            <Lozenge color="brand">
                              <Link16Regular />
                            </Lozenge>
                          </Tooltip>
                        )}
                        {hasSharedAccounts(row) && (
                          <Tooltip
                            content="Is the named Responsible person for one or more shared/departmental accounts' licenses"
                            relationship="description"
                            withArrow
                          >
                            <Lozenge color="warning">
                              <PeopleTeam16Regular />
                            </Lozenge>
                          </Tooltip>
                        )}
                        {hasExternalAccounts(row) && (
                          <Tooltip
                            content="Is the named Responsible person for one or more external accounts' licenses"
                            relationship="description"
                            withArrow
                          >
                            <Lozenge color="informative" style={EXTERNAL_BADGE_STYLE}>
                              <Building16Regular />
                            </Lozenge>
                          </Tooltip>
                        )}
                      </div>
                      {isMissingOwner(row) && (
                        <Tooltip
                          content="No department and no responsible person set — this account doesn't count as owned by anyone. Use Mark external/shared (or set a department in Entra) to fix it."
                          relationship="description"
                          withArrow
                        >
                          <Lozenge color="warning">No owner</Lozenge>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                  {serviceIds.map(id => (
                    <TableCell key={id}>
                      {(row.byService[id] ?? []).map(a => {
                        const Icon = getLicenseIcon(configByService[id]?.[a.licenseType]?.iconKey)
                        return (
                          <Tooltip
                            key={a.licenseType}
                            content={licenseTypeDisplayName(a.licenseType, configByService)}
                            relationship="label"
                          >
                            <Icon style={{ marginRight: 6, color: ATTRIBUTION_COLOR[a.resolutionKind] }} />
                          </Tooltip>
                        )
                      })}
                    </TableCell>
                  ))}
                  <TableCell>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {ownAccountByEmail.get(row.personEmail) && (
                        <Tooltip
                          content={
                            row.entraStatus === 'disabled'
                              ? `Blocked/disabled in Entra but still has live GitHub access: @${ownAccountByEmail.get(row.personEmail)!.login}`
                              : `Own GitHub account: @${ownAccountByEmail.get(row.personEmail)!.login} — ${ownAccountByEmail.get(row.personEmail)!.access.length} repo(s) with access`
                          }
                          relationship="description"
                          withArrow
                        >
                          <Lozenge color={row.entraStatus === 'disabled' ? 'danger' : 'success'}>
                            @{ownAccountByEmail.get(row.personEmail)!.login}
                          </Lozenge>
                        </Tooltip>
                      )}
                      {(responsibleForByEmail.get(row.personEmail) ?? []).map(u => (
                        <Tooltip
                          key={u.login}
                          content={`Responsible for ${u.link?.status} GitHub account: @${u.login}`}
                          relationship="description"
                          withArrow
                        >
                          <Lozenge color={u.link?.status === 'shared' ? 'warning' : 'informative'}>@{u.login}</Lozenge>
                        </Tooltip>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredRows.length === 0 && <p>No people match the current filters.</p>}
        </>
      )}
    </div>
  )
}
